import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { fetchAdminUsers } from '../../../store/slices/authSlice';
import { apiServices } from '../../../services/apiServices';
import Swal from 'sweetalert2';

// Configure SweetAlert2 to have higher z-index
Swal.mixin({
  customClass: {
    container: 'swal2-container-high-z'
  }
});

const UserAccess = () => {
  const dispatch = useDispatch();
  
  // Get admin users data from Redux store
  const { adminUsers, adminUsersLoading, adminUsersError, adminUsersPagination } = useSelector(
    (state) => state.auth
  );

  // Add CSS for high z-index SweetAlert
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .swal2-container-high-z {
        z-index: 99999 !important;
      }
      .swal2-container {
        z-index: 99999 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  
  // Form state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: '',
    modules: [],
    custom_role_name: '',
    custom_role_description: '',
    is_active: true
  });

  // Modules based on selected role (populated by /serviceuat/admin/role-module-access-rolewise)
  const [availableModules, setAvailableModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);

  // User roles from API (populated by /serviceuat/user-roles)
  const [userRoles, setUserRoles] = useState([]);
  const [userRolesLoading, setUserRolesLoading] = useState(false);

  // Fetch user roles for dropdown
  useEffect(() => {
    let cancelled = false;
    setUserRolesLoading(true);
    apiServices
      .getAPI('user-roles')
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const list = Array.isArray(data?.roles)
          ? data.roles
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
        const normalized = list.map((r) => {
          if (typeof r === 'string') return { value: r, label: r, user_role_id: null };
          const id = r.user_role_id ?? r.role_id ?? r.id ?? r.value;
          const name = r.role_name ?? r.name ?? r.label ?? String(id ?? '');
          return { value: id, label: name, user_role_id: id };
        });
        setUserRoles(normalized);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch user roles:', err);
          setUserRoles([]);
        }
      })
      .finally(() => {
        if (!cancelled) setUserRolesLoading(false);
      });
    return () => { cancelled = true; };
  }, []);


  // Fetch modules based on selected role
  useEffect(() => {
    if (!formData.role) {
      setAvailableModules([]);
      setModulesLoading(false);
      return;
    }

    let cancelled = false;

    // If Custom is selected, fetch all available modules from /modules API
    if (formData.role === 'CUSTOM') {
      setModulesLoading(true);
      apiServices
        .getAPI('modules')
        .then((res) => {
          if (cancelled) return;
          const data = res?.data;
          const modules = Array.isArray(data?.modules)
            ? data.modules
            : Array.isArray(data?.data)
              ? data.data
              : Array.isArray(data)
                ? data
                : [];
          
          // Extract module names - handle both string arrays and object arrays
          const moduleNames = modules.map((m) => {
            if (typeof m === 'string') return m;
            return m.module_name ?? m.name ?? m.label ?? String(m);
          });
          
          setAvailableModules(moduleNames);
          // Don't auto-select modules for custom role
          setFormData((prev) => ({
            ...prev,
            modules: []
          }));
        })
        .catch((err) => {
          if (!cancelled) {
            console.error('Failed to fetch modules:', err);
            setAvailableModules([]);
          }
        })
        .finally(() => {
          if (!cancelled) setModulesLoading(false);
        });
      return () => { cancelled = true; };
    }

    // For regular roles, fetch modules based on role
    let cancelled2 = false;
    
    // Get user_role_id from the selected role
    // formData.role contains the user_role_id (stored as value)
    const user_role_id = parseInt(formData.role, 10);
    
    // If not a valid integer, don't make the API call
    if (isNaN(user_role_id)) {
      setAvailableModules([]);
      setModulesLoading(false);
      return;
    }

    setModulesLoading(true);
    apiServices
      .postAPI('admin/role-module-access-rolewise', { user_role_id })
      .then((res) => {
        if (cancelled2) return;
        const data = res?.data;
        
        if (data?.success && Array.isArray(data?.modules)) {
          // Extract module names from the modules array
          const moduleNames = data.modules.map((module) => module.module_name);
          setAvailableModules(moduleNames);
          
          // Auto-select all modules returned from the API
          setFormData((prev) => ({
            ...prev,
            modules: moduleNames
          }));
        } else {
          setAvailableModules([]);
          setFormData((prev) => ({
            ...prev,
            modules: []
          }));
        }
      })
      .catch((err) => {
        if (!cancelled2) {
          console.error('Failed to fetch modules for role:', err);
          setAvailableModules([]);
          setFormData((prev) => ({
            ...prev,
            modules: []
          }));
        }
      })
      .finally(() => {
        if (!cancelled2) setModulesLoading(false);
      });
    
    return () => { cancelled2 = true; };
  }, [formData.role]);

  // Fetch admin users using Redux thunk
  const fetchAdmins = () => {
    dispatch(fetchAdminUsers({
      page: page + 1,
      limit: rowsPerPage,
      search: searchTerm
    }));
  };

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  // Show error alert if there's an error
  useEffect(() => {
    if (adminUsersError) {
      Swal.fire({
        title: 'Error',
        text: adminUsersError,
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
    }
  }, [adminUsersError]);

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Handle search on Enter key
  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      fetchAdmins();
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Toggle row expansion
  const handleToggleExpand = (adminId) => {
    setExpandedRows(prev => ({
      ...prev,
      [adminId]: !prev[adminId]
    }));
  };

  // Handle view action
  const handleView = (admin) => {
    handleToggleExpand(admin.admin_id);
  };

  // Handle edit action
  const handleEdit = (admin) => {
    setEditingUser(admin);
    
    // Map role name to role ID if needed
    let roleValue = admin.role_id || '';
    if (!roleValue && admin.role) {
      // If role is a string, find matching role in userRoles
      const matchedRole = userRoles.find(r => 
        r.label === admin.role || r.value === admin.role
      );
      if (matchedRole) {
        roleValue = matchedRole.value;
      } else if (admin.role === 'CUSTOM' || admin.role === 'Custom') {
        roleValue = 'CUSTOM';
      }
    }
    
    // Populate form with existing user data
    setFormData({
      full_name: admin.full_name || '',
      email: admin.email || '',
      phone: admin.phone || '',
      role: roleValue,
      modules: admin.permissions?.map(p => p.module_name) || [],
      custom_role_name: admin.custom_role_name || '',
      custom_role_description: admin.custom_role_description || '',
      is_active: admin.is_active !== undefined ? admin.is_active : true
    });
    
    // Fetch modules for the selected role
    if (roleValue) {
      const user_role_id = parseInt(roleValue, 10);
      if (!isNaN(user_role_id) && roleValue !== 'CUSTOM') {
        setModulesLoading(true);
        apiServices
          .postAPI('admin/role-module-access-rolewise', { user_role_id })
          .then((res) => {
            const data = res?.data;
            if (data?.success && Array.isArray(data?.modules)) {
              const moduleNames = data.modules.map((module) => module.module_name);
              setAvailableModules(moduleNames);
            }
          })
          .catch((err) => {
            console.error('Failed to fetch modules for role:', err);
            setAvailableModules([]);
          })
          .finally(() => {
            setModulesLoading(false);
          });
      } else if (roleValue === 'CUSTOM') {
        // For custom roles, fetch all modules
        setModulesLoading(true);
        apiServices
          .getAPI('modules')
          .then((res) => {
            const data = res?.data;
            const modules = Array.isArray(data?.modules)
              ? data.modules
              : Array.isArray(data?.data)
                ? data.data
                : Array.isArray(data)
                  ? data
                  : [];
            const moduleNames = modules.map((m) => {
              if (typeof m === 'string') return m;
              return m.module_name ?? m.name ?? m.label ?? String(m);
            });
            setAvailableModules(moduleNames);
          })
          .catch((err) => {
            console.error('Failed to fetch modules:', err);
            setAvailableModules([]);
          })
          .finally(() => {
            setModulesLoading(false);
          });
      }
    }
    
    setEditModalOpen(true);
  };

  // Handle open create modal
  const handleOpenCreateModal = () => {
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      role: '',
      modules: [],
      custom_role_name: '',
      custom_role_description: ''
    });
    setCreateModalOpen(true);
  };

  // Handle close create modal
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      role: '',
      modules: [],
      custom_role_name: '',
      custom_role_description: '',
      is_active: true
    });
    setAvailableModules([]);
  };

  // Handle close edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingUser(null);
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      role: '',
      modules: [],
      custom_role_name: '',
      custom_role_description: '',
      is_active: true
    });
    setAvailableModules([]);
  };

  // Handle form field change
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle update admin user
  const handleUpdateAdminUser = async () => {
    if (!editingUser) return;

    // Validation
    if (!formData.full_name.trim()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please enter full name',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    if (!formData.phone.trim()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please enter phone number',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    if (!formData.role) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please select a role',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    // Validate custom role fields if Custom is selected
    if (formData.role === 'CUSTOM') {
      if (!formData.custom_role_name.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Please enter custom role name',
          icon: 'error',
          customClass: {
            container: 'swal2-container-high-z'
          }
        });
        return;
      }
      if (!formData.custom_role_description.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Please enter role description',
          icon: 'error',
          customClass: {
            container: 'swal2-container-high-z'
          }
        });
        return;
      }
    }

    if (formData.modules.length === 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please select at least one module',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    setUpdating(true);
    try {
      const adminDataStr = localStorage.getItem('adminData');
      const adminData = adminDataStr ? JSON.parse(adminDataStr) : null;
      const updatedBy = adminData?.admin_id || adminData?.username || '1';

      const payload = {
        full_name: formData.full_name.trim(),
        phone: formData.phone.trim(),
        role: formData.role,
        modules: formData.modules,
        is_active: formData.is_active,
        updated_by: updatedBy
      };

      // Add custom role fields if Custom is selected
      if (formData.role === 'CUSTOM') {
        payload.custom_role_name = formData.custom_role_name.trim();
        payload.custom_role_description = formData.custom_role_description.trim();
      }

      const response = await apiServices.putAPI(`admin/users/${editingUser.admin_id}`, payload);

      if (response.data.success) {
        Swal.fire({
          title: 'Success',
          text: 'Admin user updated successfully!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            container: 'swal2-container-high-z'
          }
        });
        handleCloseEditModal();
        fetchAdmins();
      } else {
        throw new Error(response.data.message || 'Failed to update admin user');
      }
    } catch (err) {
      console.error('Error updating admin user:', err);
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to update admin user',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
    } finally {
      setUpdating(false);
    }
  };

  // Handle create admin user
  const handleCreateAdminUser = async () => {
    // Validation
    if (!formData.full_name.trim()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please enter full name',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    if (!formData.email.trim()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please enter email',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    if (!formData.phone.trim()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please enter phone number',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    if (!formData.role) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please select a role',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    // Validate custom role fields if Custom is selected
    if (formData.role === 'CUSTOM') {
      if (!formData.custom_role_name.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Please enter custom role name',
          icon: 'error',
          customClass: {
            container: 'swal2-container-high-z'
          }
        });
        return;
      }
      if (!formData.custom_role_description.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Please enter role description',
          icon: 'error',
          customClass: {
            container: 'swal2-container-high-z'
          }
        });
        return;
      }
    }

    if (formData.modules.length === 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please select at least one module',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    setCreating(true);
    try {
      const adminDataStr = localStorage.getItem('adminData');
      const adminData = adminDataStr ? JSON.parse(adminDataStr) : null;
      const createdBy = adminData?.admin_id || adminData?.username || '1';

      const payload = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        role: formData.role,
        modules: formData.modules,
        created_by: createdBy
      };

      // Add custom role fields if Custom is selected
      if (formData.role === 'CUSTOM') {
        payload.custom_role_name = formData.custom_role_name.trim();
        payload.custom_role_description = formData.custom_role_description.trim();
      }

      const response = await apiServices.postAPI('admin/users', payload);

      if (response.data.success) {
        Swal.fire({
          title: 'Success',
          text: 'Admin user created successfully!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            container: 'swal2-container-high-z'
          }
        });
        handleCloseCreateModal();
        fetchAdmins();
      } else {
        throw new Error(response.data.message || 'Failed to create admin user');
      }
    } catch (err) {
      console.error('Error creating admin user:', err);
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to create admin user',
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
    } finally {
      setCreating(false);
    }
  };

  // Render permissions
  const renderPermissions = (permissions) => {
    if (!permissions || permissions.length === 0) {
      return <Typography variant="body2" color="text.secondary">No permissions</Typography>;
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
        {permissions.map((permission, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 150 }}>
              {permission.module_name}:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {permission.can_view && (
                <Chip label="View" size="small" color="success" />
              )}
              {permission.can_create && (
                <Chip label="Create" size="small" color="info" />
              )}
              {permission.can_update && (
                <Chip label="Update" size="small" color="warning" />
              )}
              {permission.can_delete && (
                <Chip label="Delete" size="small" color="error" />
              )}
              {!permission.can_view && !permission.can_create && 
               !permission.can_update && !permission.can_delete && (
                <Chip label="No Access" size="small" color="default" />
              )}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', p: 2, pr: 4, mr: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mr: 2 }}>
        <Typography variant="h4" sx={{ color: '#214F7D' }}>
          User Access Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateModal}
          sx={{
            backgroundColor: '#214F7D',
            '&:hover': {
              backgroundColor: '#1a3d5c'
            }
          }}
        >
          Add New
        </Button>
      </Box>

      {/* Search and Filter Card */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', mr: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                fullWidth
                placeholder="Search by username, email, or full name..."
                value={searchTerm}
                onChange={handleSearch}
                onKeyPress={handleSearchKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ backgroundColor: '#fff' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                <Button
                  variant="contained"
                  onClick={fetchAdmins}
                  sx={{ 
                    minWidth: 120,
                    backgroundColor: '#214F7D',
                    '&:hover': {
                      backgroundColor: '#1a3d5c'
                    }
                  }}
                >
                  Search
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {adminUsersError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {adminUsersError}
        </Alert>
      )}

      {/* Admin Users Table */}
      <Paper sx={{ width: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'auto', pr: 2, mr: 2 }}>
        <TableContainer sx={{ maxHeight: 600, overflowX: 'auto', overflowY: 'auto' }}>
          <Table stickyHeader sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 200 }}>Full Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 250 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 180 }}>Modules</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 140 }}>Created At</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 150 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adminUsersLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                      Loading admin users...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : adminUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography variant="body1" color="text.secondary">
                      No admin users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                adminUsers.map((admin) => (
                  <React.Fragment key={admin.admin_id}>
                    <TableRow 
                      hover 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{admin.full_name}</TableCell>
                      <TableCell>{admin.email}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{admin.role}</TableCell>
                      <TableCell sx={{ fontSize: '0.875rem', maxWidth: 200 }}>
                        {admin.permissions?.length
                          ? admin.permissions.map((p) => p.module_name || p).join(', ')
                          : '—'}
                      </TableCell>
                      
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {admin.created_at
                          ? new Date(admin.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                          : admin.created_at_date
                            ? new Date(admin.created_at_date).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                            : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={admin.is_active ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={admin.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View">
                            <IconButton 
                              size="small"
                              onClick={() => handleView(admin)}
                              sx={{
                                color: '#214F7D',
                                '&:hover': {
                                  backgroundColor: 'rgba(33, 79, 125, 0.1)'
                                }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton 
                              size="small"
                              onClick={() => handleEdit(admin)}
                              sx={{
                                color: '#214F7D',
                                '&:hover': {
                                  backgroundColor: 'rgba(33, 79, 125, 0.1)'
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell 
                        style={{ paddingBottom: 0, paddingTop: 0 }} 
                        colSpan={7}
                        sx={{ backgroundColor: '#fafafa' }}
                      >
                        <Collapse in={expandedRows[admin.admin_id]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ color: '#214F7D', mb: 2 }}>
                              Permissions for {admin.full_name || admin.username}
                            </Typography>
                            {renderPermissions(admin.permissions)}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={adminUsersPagination.total || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: '1px solid #e0e0e0' }}
        />
      </Paper>

      {/* Create Admin User Modal */}
      <Dialog
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 3,
          pt: 4,
          px: 4,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#214F7D' }}>
            Add New Admin User
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pb: 2, pt: '32px !important', px: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Row 1: Full Name | Email */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.full_name}
                onChange={(e) => handleFormChange('full_name', e.target.value)}
                required
                placeholder="Enter full name"
                sx={{ flex: 1 }}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                required
                placeholder="Enter email address"
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Row 2: Phone Number | Role */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                required
                placeholder="Enter phone number"
                sx={{ flex: 1 }}
              />
              <FormControl fullWidth required sx={{ flex: 1 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => {
                    handleFormChange('role', e.target.value);
                    // Clear custom role fields if not Custom
                    if (e.target.value !== 'CUSTOM') {
                      setFormData(prev => ({
                        ...prev,
                        custom_role_name: '',
                        custom_role_description: ''
                      }));
                    }
                  }}
                  label="Role"
                  input={<OutlinedInput label="Role" />}
                  disabled={userRolesLoading}
                >
                  {userRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="CUSTOM">Custom</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Row 3: Custom Role Fields (shown only when Custom is selected) */}
            {formData.role === 'CUSTOM' && (
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  fullWidth
                  label="Role Name"
                  value={formData.custom_role_name}
                  onChange={(e) => handleFormChange('custom_role_name', e.target.value)}
                  required
                  placeholder="Enter custom role name"
                  sx={{ flex: 1 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.custom_role_description}
                  onChange={(e) => handleFormChange('custom_role_description', e.target.value)}
                  required
                  placeholder="Enter role description"
                  sx={{ flex: 1 }}
                  multiline
                  rows={2}
                />
              </Box>
            )}

            {/* Row 4: Modules (full width) */}
            <FormControl fullWidth required>
              <InputLabel>Modules</InputLabel>
              <Select
                multiple
                value={formData.modules}
                onChange={(e) => handleFormChange('modules', e.target.value)}
                input={<OutlinedInput label="Modules" />}
                disabled={modulesLoading || !formData.role}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {modulesLoading ? 'Loading modules...' : 'Select modules'}
                      </Typography>
                    ) : (
                      selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))
                    )}
                  </Box>
                )}
              >
                {modulesLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <ListItemText primary="Loading modules..." />
                  </MenuItem>
                ) : availableModules.length === 0 ? (
                  <MenuItem disabled>
                    <ListItemText primary="No modules available for this role" />
                  </MenuItem>
                ) : (
                  availableModules.map((module) => (
                    <MenuItem key={module} value={module}>
                      <Checkbox checked={formData.modules.indexOf(module) > -1} />
                      <ListItemText primary={module} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 3, pt: 2 }}>
          <Button
            onClick={handleCloseCreateModal}
            disabled={creating}
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateAdminUser}
            variant="contained"
            disabled={creating}
            sx={{
              backgroundColor: '#214F7D',
              '&:hover': {
                backgroundColor: '#1a3d5c'
              }
            }}
          >
            {creating ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Admin User Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 3,
          pt: 4,
          px: 4,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#214F7D' }}>
            Edit Admin User
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pb: 2, pt: '32px !important', px: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Row 1: Full Name | Email */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.full_name}
                onChange={(e) => handleFormChange('full_name', e.target.value)}
                required
                placeholder="Enter full name"
                sx={{ flex: 1 }}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                disabled
                placeholder="Email cannot be changed"
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Row 2: Phone Number | Role */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                required
                placeholder="Enter phone number"
                sx={{ flex: 1 }}
              />
              <FormControl fullWidth required sx={{ flex: 1 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => {
                    handleFormChange('role', e.target.value);
                    // Clear custom role fields if not Custom
                    if (e.target.value !== 'CUSTOM') {
                      setFormData(prev => ({
                        ...prev,
                        custom_role_name: '',
                        custom_role_description: ''
                      }));
                    }
                  }}
                  label="Role"
                  input={<OutlinedInput label="Role" />}
                  disabled={userRolesLoading}
                >
                  {userRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="CUSTOM">Custom</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Row 3: Custom Role Fields (shown only when Custom is selected) */}
            {formData.role === 'CUSTOM' && (
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  fullWidth
                  label="Role Name"
                  value={formData.custom_role_name}
                  onChange={(e) => handleFormChange('custom_role_name', e.target.value)}
                  required
                  placeholder="Enter custom role name"
                  sx={{ flex: 1 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.custom_role_description}
                  onChange={(e) => handleFormChange('custom_role_description', e.target.value)}
                  required
                  placeholder="Enter role description"
                  sx={{ flex: 1 }}
                  multiline
                  rows={2}
                />
              </Box>
            )}

            {/* Row 4: Status */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Status: {formData.is_active ? 'Active' : 'Inactive'}
                  </Typography>
                }
              />
            </Box>

            {/* Row 5: Modules (full width) */}
            <FormControl fullWidth required>
              <InputLabel>Modules</InputLabel>
              <Select
                multiple
                value={formData.modules}
                onChange={(e) => handleFormChange('modules', e.target.value)}
                input={<OutlinedInput label="Modules" />}
                disabled={modulesLoading || !formData.role}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {modulesLoading ? 'Loading modules...' : 'Select modules'}
                      </Typography>
                    ) : (
                      selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))
                    )}
                  </Box>
                )}
              >
                {modulesLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <ListItemText primary="Loading modules..." />
                  </MenuItem>
                ) : availableModules.length === 0 ? (
                  <MenuItem disabled>
                    <ListItemText primary="No modules available for this role" />
                  </MenuItem>
                ) : (
                  availableModules.map((module) => (
                    <MenuItem key={module} value={module}>
                      <Checkbox checked={formData.modules.indexOf(module) > -1} />
                      <ListItemText primary={module} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 3, pt: 2 }}>
          <Button
            onClick={handleCloseEditModal}
            disabled={updating}
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateAdminUser}
            variant="contained"
            disabled={updating}
            sx={{
              backgroundColor: '#214F7D',
              '&:hover': {
                backgroundColor: '#1a3d5c'
              }
            }}
          >
            {updating ? 'Updating...' : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserAccess;
